import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { reportApi, clientApi } from '@/services/api'
import type { ProfitLoss, BalanceSheet, TrialBalance, CashSummary } from '@/types'
import { formatCurrency } from '@/utils/currency'
import { BarChart3, FileText, BookOpenCheck, CreditCard, BookOpen } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

type ReportType = 'profit-loss' | 'balance-sheet' | 'trial-balance' | 'cash-summary'

const REPORT_TABS = [
  { key: 'profit-loss' as ReportType, label: 'Profit & Loss', icon: BarChart3 },
  { key: 'balance-sheet' as ReportType, label: 'Balance Sheet', icon: FileText },
  { key: 'trial-balance' as ReportType, label: 'Trial Balance', icon: BookOpenCheck },
  { key: 'cash-summary' as ReportType, label: 'Cash Summary', icon: CreditCard },
]

export default function ClientReports() {
  const { clientId } = useParams<{ clientId: string }>()
  const [activeTab, setActiveTab] = useState<ReportType>('profit-loss')
  const [loading, setLoading] = useState(false)
  const [startDate, setStartDate] = useState(() => `${new Date().getFullYear()}-01-01`)
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0])
  const [asAt, setAsAt] = useState(() => new Date().toISOString().split('T')[0])
  const [pl, setPl] = useState<ProfitLoss | null>(null)
  const [bs, setBs] = useState<BalanceSheet | null>(null)
  const [tb, setTb] = useState<TrialBalance | null>(null)
  const [cs, setCs] = useState<CashSummary | null>(null)
  const [currency, setCurrency] = useState('USD')

  useEffect(() => {
    if (clientId) clientApi.get(clientId).then((r) => setCurrency(r.data.client.currency)).catch(() => {})
    generate()
  }, [clientId, activeTab])

  async function generate() {
    if (!clientId) return
    setLoading(true)
    try {
      if (activeTab === 'profit-loss') {
        const res = await reportApi.profitLoss(clientId, startDate, endDate)
        setPl(res.data)
      } else if (activeTab === 'balance-sheet') {
        const res = await reportApi.balanceSheet(clientId, asAt)
        setBs(res.data)
      } else if (activeTab === 'trial-balance') {
        const res = await reportApi.trialBalance(clientId, asAt)
        setTb(res.data)
      } else {
        const res = await reportApi.cashSummary(clientId, startDate, endDate)
        setCs(res.data)
      }
    } finally {
      setLoading(false)
    }
  }

  const dateControls = activeTab === 'balance-sheet' || activeTab === 'trial-balance' ? (
    <div className="flex gap-3 items-end mb-5">
      <div>
        <Label>As At</Label>
        <Input type="date" value={asAt} onChange={(e) => setAsAt(e.target.value)} className="py-1.5" />
      </div>
      <Button onClick={generate}>Generate</Button>
    </div>
  ) : (
    <div className="flex gap-3 items-end mb-5">
      <div>
        <Label>From</Label>
        <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="py-1.5" />
      </div>
      <div>
        <Label>To</Label>
        <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="py-1.5" />
      </div>
      <Button onClick={generate}>Generate</Button>
    </div>
  )

  return (
    <div className="min-h-screen bg-surface-50">
      {/* Header */}
      <div className="bg-white border-b border-surface-200 px-6 py-4 flex items-center gap-3">
        <div className="w-8 h-8 bg-[#0078C8] rounded-lg flex items-center justify-center">
          <BookOpen className="w-4 h-4 text-white" />
        </div>
        <div>
          <h1 className="text-[17px] font-bold text-[#000A1E] leading-[24px]">Client Reports</h1>
          <p className="text-xs text-[rgba(0,10,30,0.75)]">Financial statements</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-6">
        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-surface-100 p-1 rounded-lg w-fit">
          {REPORT_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
                activeTab === tab.key
                  ? 'bg-white text-[#0078C8] shadow-sm'
                  : 'text-[rgba(0,10,30,0.75)] hover:text-[#000A1E]'
              )}
            >
              <tab.icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          ))}
        </div>

        {dateControls}

        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-5 h-5 border-2 border-[#0078C8] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {activeTab === 'profit-loss' && pl && (
              <div className="card overflow-hidden">
                <div className="px-5 py-3 border-b border-surface-200 bg-surface-50">
                  <p className="text-xs text-[rgba(0,10,30,0.75)]">{pl.start_date} to {pl.end_date}</p>
                </div>
                <div className="border-b border-surface-200">
                  <div className="px-5 py-2.5 bg-emerald-50/50 border-b border-surface-100">
                    <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wider">Revenue</p>
                  </div>
                  <table className="w-full">
                    <tbody>
                      {pl.revenue.accounts.map((acc) => (
                        <tr key={acc.account_id} className="border-b border-surface-50">
                          <td className="px-5 py-2.5 text-xs text-[rgba(0,10,30,0.75)] font-mono w-20">{acc.account_code}</td>
                          <td className="px-2 py-2.5 text-[13px] text-[#000A1E]">{acc.account_name}</td>
                          <td className="px-5 py-2.5 text-[13px] text-right tabular-nums">{formatCurrency(acc.net, currency)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-emerald-50/30">
                        <td colSpan={2} className="px-5 py-3 text-[13px] font-semibold text-[#000A1E]">Total Revenue</td>
                        <td className="px-5 py-3 text-[13px] font-bold text-right text-emerald-700">{formatCurrency(pl.revenue.total, currency)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <div className="border-b border-surface-200">
                  <div className="px-5 py-2.5 bg-red-50/50 border-b border-surface-100">
                    <p className="text-xs font-semibold text-red-700 uppercase tracking-wider">Expenses</p>
                  </div>
                  <table className="w-full">
                    <tbody>
                      {pl.expenses.accounts.map((acc) => (
                        <tr key={acc.account_id} className="border-b border-surface-50">
                          <td className="px-5 py-2.5 text-xs text-[rgba(0,10,30,0.75)] font-mono w-20">{acc.account_code}</td>
                          <td className="px-2 py-2.5 text-[13px] text-[#000A1E]">{acc.account_name}</td>
                          <td className="px-5 py-2.5 text-[13px] text-right tabular-nums">{formatCurrency(acc.net, currency)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-red-50/30">
                        <td colSpan={2} className="px-5 py-3 text-[13px] font-semibold text-[#000A1E]">Total Expenses</td>
                        <td className="px-5 py-3 text-[13px] font-bold text-right text-red-700">{formatCurrency(pl.expenses.total, currency)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <div className="px-5 py-4 bg-surface-50 flex justify-between items-center">
                  <span className="text-base font-bold text-[#000A1E]">Net Profit</span>
                  <span className={cn('text-lg font-bold tabular-nums', parseFloat(pl.net_profit) >= 0 ? 'text-emerald-700' : 'text-red-700')}>
                    {formatCurrency(pl.net_profit, currency)}
                  </span>
                </div>
              </div>
            )}

            {activeTab === 'balance-sheet' && bs && (
              <div className="card overflow-hidden">
                <div className="px-5 py-3 border-b border-surface-200 bg-surface-50">
                  <p className="text-xs text-[rgba(0,10,30,0.75)]">As at {bs.as_at}</p>
                </div>
                {[
                  { section: bs.assets, label: 'Assets', color: 'brand' },
                  { section: bs.liabilities, label: 'Liabilities', color: 'orange' },
                  { section: bs.equity, label: 'Equity', color: 'purple' },
                ].map(({ section, label }) => (
                  <div key={label} className="border-b border-surface-200">
                    <div className="px-5 py-2.5 bg-surface-50 border-b border-surface-100">
                      <p className="text-xs font-semibold text-[rgba(0,10,30,0.75)] uppercase tracking-wider">{label}</p>
                    </div>
                    <table className="w-full">
                      <tbody>
                        {section.accounts.map((acc, i) => (
                          <tr key={acc.account_id || i} className="border-b border-surface-50">
                            <td className="px-5 py-2.5 text-xs text-[rgba(0,10,30,0.75)] font-mono w-20">{acc.account_code}</td>
                            <td className="px-2 py-2.5 text-[13px] text-[#000A1E]">{acc.account_name}</td>
                            <td className="px-5 py-2.5 text-[13px] text-right tabular-nums">{formatCurrency(acc.net, currency)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-surface-100/50">
                          <td colSpan={2} className="px-5 py-3 text-[13px] font-semibold text-[#000A1E]">Total {label}</td>
                          <td className="px-5 py-3 text-[13px] font-bold text-right tabular-nums">{formatCurrency(section.total, currency)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'trial-balance' && tb && (
              <div className="card overflow-hidden">
                <div className="px-5 py-3 border-b border-surface-200 bg-surface-50">
                  <p className="text-xs text-[rgba(0,10,30,0.75)]">As at {tb.as_at}</p>
                </div>
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-surface-100">
                      <th className="table-header">Code</th>
                      <th className="table-header">Account</th>
                      <th className="table-header text-right">Debit</th>
                      <th className="table-header text-right">Credit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-surface-50">
                    {tb.lines.map((line) => (
                      <tr key={line.account_id}>
                        <td className="px-5 py-2.5 text-xs font-mono text-[rgba(0,10,30,0.75)]">{line.account_code}</td>
                        <td className="px-2 py-2.5 text-[13px] text-[#000A1E]">{line.account_name}</td>
                        <td className="px-5 py-2.5 text-[13px] text-right tabular-nums">{parseFloat(line.debit) > 0 ? formatCurrency(line.debit, currency) : ''}</td>
                        <td className="px-5 py-2.5 text-[13px] text-right tabular-nums">{parseFloat(line.credit) > 0 ? formatCurrency(line.credit, currency) : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-surface-200 bg-surface-50">
                      <td colSpan={2} className="px-5 py-3 text-[13px] font-semibold text-[#000A1E]">Totals</td>
                      <td className="px-5 py-3 text-[13px] font-bold text-right tabular-nums">{formatCurrency(tb.total_debit, currency)}</td>
                      <td className="px-5 py-3 text-[13px] font-bold text-right tabular-nums">{formatCurrency(tb.total_credit, currency)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            {activeTab === 'cash-summary' && cs && (
              <div className="card overflow-hidden">
                <div className="px-5 py-3 border-b border-surface-200 bg-surface-50">
                  <p className="text-xs text-[rgba(0,10,30,0.75)]">{cs.start_date} to {cs.end_date}</p>
                </div>
                <div className="divide-y divide-surface-100">
                  {[
                    { label: 'Opening Balance', value: cs.opening_balance, muted: true },
                    { label: 'Total Received', value: cs.total_received, positive: true },
                    { label: 'Total Spent', value: cs.total_spent, negative: true },
                    { label: 'Closing Balance', value: cs.closing_balance, bold: true },
                  ].map((row) => (
                    <div key={row.label} className="px-5 py-3.5 flex justify-between items-center">
                      <span className={cn('text-[13px]', row.bold ? 'font-bold text-[#000A1E]' : 'text-[rgba(0,10,30,0.75)]')}>{row.label}</span>
                      <span className={cn(
                        'text-[13px] tabular-nums font-medium',
                        row.bold && 'text-base font-bold',
                        row.positive && 'text-emerald-700',
                        row.negative && 'text-red-700',
                      )}>
                        {formatCurrency(row.value, currency)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
